/**
 * viewport — runtime accessor for the current screen-space viewport size.
 *
 * Full-screen effects (screen flash, vignette, death overlay) live on the
 * Pixi overlay's screen-space UI container, which is sized by
 * `resizeTo: window` (see `PixiOverlayHost`). The UI container is NOT
 * camera-transformed, so a full-canvas rectangle must use the *live canvas
 * pixel size*, NOT the camera's "intended frame" (`camera.width/height`,
 * default 1280×720 — that's the letterboxed world frame, not the canvas).
 *
 * `window.innerWidth/innerHeight` is the logical (CSS-pixel) size that
 * Pixi's `resizeTo: window` + `autoDensity` keeps `app.screen` in lockstep
 * with, so it's the correct source for screen-space overlay extents and it
 * re-reads naturally on every resize. We avoid reaching into the overlay's
 * private `app` so this stays a zero-coupling runtime read.
 *
 * Lane 2 of the VFX & Rendering Parity arc (2026-06-03): replaces the
 * hardcoded `1280×720` fallback in ScreenEffectManager.screenFlash that
 * drew flashes as a fixed rectangle instead of covering the live viewport.
 */

export interface ViewportSize {
  width: number;
  height: number;
}

/** Last-resort dimensions when no window/document is available (e.g. SSR/test). */
const HEADLESS_FALLBACK: ViewportSize = { width: 1280, height: 720 };

/**
 * Read the current viewport size from the live window. Re-reads on every
 * call, so callers naturally pick up window resizes. Falls back to a
 * neutral size only in non-browser environments.
 */
export function getViewportSize(): ViewportSize {
  if (typeof window === 'undefined') return { ...HEADLESS_FALLBACK };
  // innerWidth/innerHeight track resizeTo:window logical pixels; clamp to
  // >0 so a degenerate (0) window never produces a zero-area overlay.
  const width = window.innerWidth || HEADLESS_FALLBACK.width;
  const height = window.innerHeight || HEADLESS_FALLBACK.height;
  return { width, height };
}
